import React from 'react'
import { useLocation } from 'react-router-dom'
import { type TranslationKey, useT } from '@/i18n/translations'
import { useJoinEffectiveLanguage } from '@/hooks/useJoinEffectiveLanguage'

type Variant =
  | 'expired'
  | 'cancelled'
  | 'completed'
  | 'failed'
  | 'in_progress'
  | 'link_invalid'
  | 'network_error'

interface LocationState {
  variant?: Variant
  occurredAt?: string
  errorCode?: string
}

const VARIANT_TO_KEY: Record<Variant, TranslationKey> = {
  expired: 'joinStatusExpired',
  cancelled: 'joinStatusCancelled',
  completed: 'joinStatusCompleted',
  failed: 'joinStatusFailed',
  in_progress: 'joinStatusInProgress',
  link_invalid: 'joinStatusLinkInvalid',
  network_error: 'joinStatusNetworkError',
}

const VARIANT_ICONS: Record<Variant, React.ReactNode> = {
  completed: (
    <svg viewBox="0 0 24 24" className="h-12 w-12 text-emerald-400" fill="none" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  ),
  expired: (
    <svg viewBox="0 0 24 24" className="h-12 w-12 text-amber-400" fill="none" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  cancelled: (
    <svg viewBox="0 0 24 24" className="h-12 w-12 text-neutral-400" fill="none" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  ),
  failed: (
    <svg viewBox="0 0 24 24" className="h-12 w-12 text-rose-400" fill="none" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
    </svg>
  ),
  in_progress: (
    <svg viewBox="0 0 24 24" className="h-12 w-12 text-sky-400" fill="none" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  link_invalid: (
    <svg viewBox="0 0 24 24" className="h-12 w-12 text-neutral-400" fill="none" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5m-1.328-4.328a4 4 0 015.656 0l3-3a4 4 0 10-5.656-5.656l-1.5 1.5" />
    </svg>
  ),
  network_error: (
    <svg viewBox="0 0 24 24" className="h-12 w-12 text-neutral-400" fill="none" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
}

export function StatusScreen() {
  const location = useLocation()
  const state = (location.state ?? {}) as LocationState
  const variant: Variant = state.variant ?? 'link_invalid'
  const resolvedLang = useJoinEffectiveLanguage()
  const t = useT(resolvedLang)

  const message = t(VARIANT_TO_KEY[variant])

  return (
    <div className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center gap-6 px-6 py-12 text-center">
      {VARIANT_ICONS[variant]}
      <p className="text-lg leading-relaxed text-neutral-100">{message}</p>
      {state.occurredAt && (
        <p className="text-xs text-neutral-500">
          {new Date(state.occurredAt).toLocaleString()}
        </p>
      )}
      {state.errorCode && (
        <p className="text-xs text-neutral-500">Code: {state.errorCode}</p>
      )}
    </div>
  )
}